import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import { alpha, useTheme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';

import { Iconify } from 'src/components/iconify';

// ----------------------------------------------------------------------

export const PLAN_TIERS = ['free', 'starter', 'scale', 'professional'] as const;
export type PlanTag = (typeof PLAN_TIERS)[number];

export type Plan = {
  name: string;
  tag: PlanTag;
  description: string;
  price: string | number;
  priceAnnual?: string | number;
  priceSuffix?: string;
  popular?: boolean;
  enterprise?: boolean;
  previousPlanLabel?: string;
  features: (string | { text: string; highlight?: boolean; suffix?: string })[];
};

export const PLANS: Plan[] = [
  // Free plan hidden from the selection grid — users return to free by cancelling their subscription
  // {
  //   name: 'Free',
  //   tag: 'free',
  //   description: 'Track jobs and candidates with a full ATS — no cost, no time limit.',
  //   price: 0,
  //   priceAnnual: 0,
  //   priceSuffix: '/mo',
  //   features: [
  //     { text: '1', highlight: false, suffix: ' Active Job' },
  //     { text: 'Unlimited', highlight: true, suffix: ' Candidates' },
  //     { text: 'Branded Career Page', highlight: false },
  //     'Automatic Resume Import',
  //     'Pipeline & Tracking',
  //   ],
  // },
  {
    name: 'Starter',
    tag: 'starter',
    description: 'AI screens every resume so you only see the best-fit candidates.',
    price: 29,
    priceAnnual: 23,
    priceSuffix: '/mo',
    previousPlanLabel: 'Everything in Free, plus:',
    features: [
      { text: '3', highlight: false, suffix: ' Active Jobs' },
      { text: '4,000', highlight: false, suffix: ' AI Resume Evaluations /mo' },
      'Fraud & Impersonation Detection',
      'Candidate Background Insights',
      'Automated Emails',
    ],
  },
  {
    name: 'Scale',
    tag: 'scale',
    description: 'AI conducts your HR screening interviews — 24/7, at any volume.',
    price: 99,
    priceAnnual: 79,
    priceSuffix: '/mo',
    popular: true,
    previousPlanLabel: 'Everything in Starter, plus:',
    features: [
      { text: '5', highlight: false, suffix: ' Active Jobs' },
      { text: '10,000', highlight: false, suffix: ' AI Resume Evaluations /mo' },
      { text: '100', highlight: false, suffix: ' HR Screening Interviews /mo · 30 min each' },
      'Built-in Fraud Detection',
    ],
  },
  {
    name: 'Professional',
    tag: 'professional',
    description: 'Behavioral, coding, and technical interviews — the full AI suite.',
    price: 499,
    priceAnnual: 399,
    priceSuffix: '/mo',
    previousPlanLabel: 'Everything in Scale, plus:',
    features: [
      { text: '25', highlight: false, suffix: ' Active Jobs' },
      { text: '25,000', highlight: false, suffix: ' AI Resume Evaluations /mo' },
      { text: '150', highlight: false, suffix: ' HR Interviews /mo · 60 min' },
      { text: '50', highlight: false, suffix: ' Advanced Interviews /mo · 90 min' },
      'Behavioral Interviews',
      'Core Interviews',
      'White Labeling',
      'Integrations',
      { text: 'AI-Managed Pipeline', highlight: false, suffix: ' Add-on' },
    ],
  },
];

// ----------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------

export function getTierIndex(tag: string): number {
  const idx = PLAN_TIERS.indexOf(tag as PlanTag);
  return idx === -1 ? 0 : idx;
}

export function getButtonLabel(planTag: PlanTag, currentPlan: string | null): string {
  if (!currentPlan || currentPlan === 'free') {
    return planTag === 'free' ? 'Current Plan' : 'Upgrade';
  }
  if (planTag === currentPlan) return 'Current Plan';
  return getTierIndex(planTag) > getTierIndex(currentPlan) ? 'Upgrade' : 'Downgrade';
}

export function getButtonVariant(
  planTag: PlanTag,
  currentPlan: string | null
): 'contained' | 'outlined' {
  if (planTag === currentPlan) return 'outlined';
  const plan = PLANS.find((p) => p.tag === planTag);
  if (plan?.popular) return 'contained';
  return 'outlined';
}

// ----------------------------------------------------------------------
// PlanCard
// ----------------------------------------------------------------------

type PlanCardProps = {
  plan: Plan;
  isAnnual: boolean;
  currentPlan: string | null;
  isLoading: boolean;
  isSubscribing: boolean;
  onSubscribe: (tag: PlanTag) => void;
  showEventsTrialBadge?: boolean;
};

function PlanCard({
  plan,
  isAnnual,
  currentPlan,
  isLoading,
  isSubscribing,
  onSubscribe,
  showEventsTrialBadge = false,
}: PlanCardProps) {
  const theme = useTheme();
  const isEnterprise = !!plan.enterprise;
  const isPopular = !!plan.popular;
  const isCurrentPlan = currentPlan === plan.tag;
  const showTrialUI = showEventsTrialBadge && plan.tag !== 'free';

  const displayPrice = isAnnual && plan.priceAnnual !== undefined ? plan.priceAnnual : plan.price;
  const baseButtonLabel = getButtonLabel(plan.tag, currentPlan);
  const buttonLabel = showTrialUI && !isCurrentPlan ? 'Start Free Trial' : baseButtonLabel;
  const buttonVariant = getButtonVariant(plan.tag, currentPlan);

  const cardSx = isEnterprise
    ? {
        bgcolor: 'grey.900',
        color: 'common.white',
        '& .MuiTypography-root': { color: 'inherit' },
      }
    : isPopular
      ? {
          border: `2px solid ${theme.palette.primary.main}`,
          boxShadow: theme.shadows[10],
        }
      : {};

  const currentPlanSx =
    isCurrentPlan && !isPopular && !isEnterprise
      ? { border: `2px solid ${theme.palette.success.main}` }
      : {};

  const handleClick = () => {
    if (isCurrentPlan || isSubscribing) return;
    onSubscribe(plan.tag);
  };

  return (
    <Card
      sx={{
        position: 'relative',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'visible',
        ...cardSx,
        ...currentPlanSx,
      }}
    >
      {isPopular && (
        <Chip
          label="MOST POPULAR"
          color="secondary"
          size="small"
          sx={{
            position: 'absolute',
            top: -12,
            left: '50%',
            transform: 'translateX(-50%)',
            fontWeight: 700,
            fontSize: 11,
            height: 24,
            zIndex: 1,
          }}
        />
      )}

      {isCurrentPlan && (
        <Chip
          label="CURRENT PLAN"
          color="success"
          size="small"
          sx={{
            position: 'absolute',
            top: isPopular ? 14 : -12,
            left: '50%',
            transform: 'translateX(-50%)',
            fontWeight: 700,
            fontSize: 11,
            height: 24,
            zIndex: 1,
          }}
        />
      )}

      {showTrialUI && (
        <Chip
          label="FIRST MONTH FREE"
          color="success"
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            fontWeight: 700,
            fontSize: 11,
            height: 24,
            zIndex: 1,
          }}
        />
      )}

      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          p: 3,
        }}
      >
        <Typography variant="h5" sx={{ mb: 1 }}>
          {plan.name}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            mb: 3,
            minHeight: 48,
            color: isEnterprise ? alpha(theme.palette.common.white, 0.72) : 'text.secondary',
          }}
        >
          {plan.description}
        </Typography>

        <Box sx={{ mb: 3 }}>
          {typeof displayPrice === 'number' ? (
            <Stack direction="row" alignItems="flex-end" spacing={0.5}>
              <Typography variant="h2" component="span">
                ${displayPrice}
              </Typography>
              <Typography
                variant="subtitle2"
                component="span"
                sx={{
                  pb: 0.5,
                  color: isEnterprise
                    ? alpha(theme.palette.common.white, 0.6)
                    : 'text.secondary',
                }}
              >
                {plan.priceSuffix}
              </Typography>
            </Stack>
          ) : (
            <Typography variant="h2">{displayPrice}</Typography>
          )}

          {showTrialUI && typeof displayPrice === 'number' && (
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}
            >
              Free for 30 days, then ${displayPrice}/mo
            </Typography>
          )}
        </Box>

        {isLoading ? (
          <Skeleton variant="rounded" height={48} sx={{ mb: 3 }} />
        ) : (
          <Button
            fullWidth
            size="large"
            variant={buttonVariant}
            disabled={isCurrentPlan || isSubscribing}
            onClick={handleClick}
            color={isPopular ? 'primary' : isEnterprise ? 'inherit' : 'inherit'}
            sx={{
              mb: 3,
              ...(isEnterprise &&
                !isCurrentPlan && {
                  bgcolor: 'common.white',
                  color: 'grey.900',
                  '&:hover': { bgcolor: alpha(theme.palette.common.white, 0.88) },
                }),
              ...(isPopular &&
                !isCurrentPlan && {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': { bgcolor: 'primary.dark' },
                }),
              ...(!isPopular &&
                !isEnterprise &&
                !isCurrentPlan && {
                  borderColor: 'divider',
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: 'text.primary',
                    bgcolor: alpha(theme.palette.grey[500], 0.08),
                  },
                }),
            }}
          >
            {isSubscribing ? <CircularProgress size={22} color="inherit" /> : buttonLabel}
          </Button>
        )}

        {plan.previousPlanLabel ? (
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              color: isEnterprise ? alpha(theme.palette.common.white, 0.6) : 'text.disabled',
              mb: 2,
            }}
          >
            {plan.previousPlanLabel}
          </Typography>
        ) : (
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              color: 'text.disabled',
              mb: 2,
            }}
          >
            What&apos;s included:
          </Typography>
        )}

        <Stack spacing={1.5}>
          {plan.features.map((feature, index) => {
            const text = typeof feature === 'string' ? feature : feature.text;
            const suffix = typeof feature === 'string' ? '' : feature.suffix || '';
            const highlight = typeof feature === 'string' ? false : feature.highlight;

            return (
              <Stack key={index} direction="row" alignItems="flex-start" spacing={1}>
                <Iconify
                  icon="eva:checkmark-fill"
                  width={20}
                  sx={{
                    mt: 0.2,
                    flexShrink: 0,
                    color: isEnterprise
                      ? theme.palette.secondary.main
                      : isPopular
                        ? theme.palette.primary.main
                        : theme.palette.success.main,
                  }}
                />
                <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                  {highlight ? (
                    <Box
                      component="span"
                      sx={{
                        fontWeight: 700,
                        color: isEnterprise ? theme.palette.secondary.main : 'primary.main',
                      }}
                    >
                      {text}
                    </Box>
                  ) : (
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      {text}
                    </Box>
                  )}
                  {suffix && (
                    <Box
                      component="span"
                      sx={{
                        color: isEnterprise
                          ? alpha(theme.palette.common.white, 0.72)
                          : 'text.secondary',
                      }}
                    >
                      {suffix}
                    </Box>
                  )}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
}

// ----------------------------------------------------------------------
// PricingPlansGrid
// ----------------------------------------------------------------------

type PricingPlansGridProps = {
  isAnnual: boolean;
  currentPlan: string | null;
  isStatusLoading: boolean;
  loadingPlan: PlanTag | null;
  onSubscribe: (tag: PlanTag) => void;
  showEventsTrialBadge?: boolean;
};

export function PricingPlansGrid({
  isAnnual,
  currentPlan,
  isStatusLoading,
  loadingPlan,
  onSubscribe,
  showEventsTrialBadge = false,
}: PricingPlansGridProps) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 3,
        pt: 2,
        gridTemplateColumns: {
          xs: 'repeat(1, 1fr)',
          sm: 'repeat(2, 1fr)',
          lg: 'repeat(3, 1fr)',
        },
      }}
    >
      {PLANS.map((plan) => (
        <PlanCard
          key={plan.name}
          plan={plan}
          isAnnual={isAnnual}
          currentPlan={currentPlan}
          isLoading={isStatusLoading}
          isSubscribing={loadingPlan === plan.tag}
          onSubscribe={onSubscribe}
          showEventsTrialBadge={showEventsTrialBadge}
        />
      ))}
    </Box>
  );
}
